import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rpApi, api } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth-store';

// ── Types ──

export interface RPCustomer {
  id: string;
  tenantId: string;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  isActive: boolean;
  invoiceCount?: number;
  createdAt: string;
}

export interface RPInvoiceLine {
  id: string;
  description: string;
  quantity: number;
  unitPrice: string;
  amount: string;
}

export interface RPInvoice {
  id: string;
  tenantId: string;
  customerId: string;
  invoiceNumber: string;
  amount: string;
  currency: string;
  dueDate: string | null;
  status: string;
  memo: string | null;
  issuedAt: string | null;
  paidAt: string | null;
  createdAt: string;
  customer?: { name: string | null; company: string | null };
  lines?: RPInvoiceLine[];
}

export interface RPReceivedPayment {
  id: string;
  tenantId: string;
  invoiceId: string;
  customerId: string;
  referenceNumber: string;
  amount: string;
  currency: string;
  paymentMethod: string;
  status: string;
  statusMessage: string | null;
  receivedAt: string;
  createdAt: string;
  customer?: { name: string | null; company: string | null };
  invoice?: { invoiceNumber: string | null; amount: string | null };
}

export interface RPStatusHistory {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  message: string | null;
  createdAt: string;
}

export interface RPDashboardData {
  customers: { total: number; active: number };
  invoices: {
    total: number;
    byStatus: { status: string; count: number; amount: string }[];
    totalReceivable: string;
    overdue: string;
    collected: string;
  };
  payments: {
    total: number;
    totalAmount: string;
    byStatus: { status: string; count: number; amount: string }[];
    byMethod: { method: string; count: number; amount: string }[];
  };
  recentPayments: RPReceivedPayment[];
}

export interface RPCustomerCard {
  id: string;
  tenantId: string;
  customerId: string;
  customerName: string | null;
  cardLastFour: string;
  cardHolderName: string | null;
  expirationDate: string;
  cardZipCode: string;
  streetAddress: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface RPCustomerBank {
  id: string;
  tenantId: string;
  customerId: string;
  customerName: string | null;
  accountCategory: string;
  accountType: string;
  routingNumber: string;
  accountNumber: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface RPPaymentLink {
  id: string;
  tenantId: string;
  linkNumber: string;
  publicToken: string;
  customerId: string;
  invoiceId: string | null;
  amount: string;
  currency: string;
  dueDate: string | null;
  notes: string | null;
  status: string;
  locationId: string | null;
  saleTransactionId: string | null;
  isRecurring?: boolean;
  recurringFrequency?: string | null;
  recurringStartAt?: string | null;
  recurringEndAt?: string | null;
  createdAt: string;
  customer?: { name: string | null; company: string | null };
  invoice?: { invoiceNumber: string | null } | null;
}

// ── Public pay page types (no auth required) ──

export interface PublicPaymentLinkData {
  linkNumber: string;
  amount: string;
  currency: string;
  dueDate: string | null;
  notes: string | null;
  status: string;
  customerName: string | null;
  merchantName: string;
  merchantContact: string;
}

export type CardPaySubmit = {
  paymentMethod: 'card';
  cardNumber: string;
  cardHolderName: string;
  expirationDate: string;
  cvv: string;
  cardZipCode: string;
  cardStreetAddress?: string;
  email?: string;
};

export type AchPaySubmit = {
  paymentMethod: 'ach';
  firstName: string;
  lastName?: string;
  email: string;
  phone: string;
  routingNumber: string;
  accountNumber: string;
  accountType: 'checking' | 'savings';
  accountCategory: 'business' | 'personal';
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  dlState: string;
  dlNumber: string;
  authorizationType?: 'over_the_phone' | 'prearranged' | 'web';
};

export type PublicPaySubmitData = CardPaySubmit | AchPaySubmit;

export interface PaymentConfirmation {
  referenceNumber: string;
  amount: string;
  currency: string;
  status: string;
}

// ── Queries ──

export function useRPCustomers(params?: { search?: string }) {
  return useQuery({
    queryKey: ['rp', 'customers', params],
    queryFn: () => rpApi.get<{ data: RPCustomer[] }>('/customers', params),
  });
}

export function useRPInvoices(
  params?: {
    status?: string;
    customerId?: string;
    locationId?: string;
    limit?: number;
    offset?: number;
  },
  // `enabled: false` skips the request for a viewer without Invoices.
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['rp', 'invoices', params],
    queryFn: () =>
      rpApi.get<{ data: RPInvoice[]; total: number }>('/invoices', params as Record<string, string | number>),
    enabled: options?.enabled ?? true,
  });
}

export function useRPInvoiceDetail(id: string | null) {
  return useQuery({
    queryKey: ['rp', 'invoice', id],
    queryFn: () =>
      rpApi.get<{ data: RPInvoice & { lines: RPInvoiceLine[]; payments: RPReceivedPayment[] } }>(`/invoices/${id}`),
    enabled: !!id,
  });
}

/** Totals the receivepayment list endpoints return for every row matching
 *  the filters — not just the returned page. Statuses are lowercased. */
export interface RPListSummary {
  count: number;
  totalAmount: number;
  byStatus: { status: string; count: number; amount: number }[];
}

export function useRPPayments(params?: {
  status?: string;
  invoiceId?: string;
  locationId?: string;
  /** ISO-8601 instants, inclusive, matched against receivedAt. */
  startDate?: string;
  endDate?: string;
  paymentMethod?: string;
  perPage?: number;
}) {
  // The receivepayment /payments route reads `limit`, not `perPage`.
  const { perPage, ...rest } = params ?? {};
  const query = { ...rest, ...(perPage ? { limit: perPage } : {}) };
  return useQuery({
    queryKey: ['rp', 'payments', query],
    queryFn: () => rpApi.get<{ data: RPReceivedPayment[]; total: number; summary?: RPListSummary }>('/payments', query),
  });
}

export function useRPDashboard(params?: { locationId?: string }) {
  return useQuery({
    queryKey: ['rp', 'dashboard', params],
    queryFn: () => rpApi.get<{ data: RPDashboardData }>('/dashboard', params as Record<string, string>),
  });
}

export interface RPFundingEntry {
  funding_date: string;
  payment_method: string;
  transaction_count: string;
  total_amount: string;
  currency: string;
}

export interface RPFundingSummaryParams {
  merchantId?: string;
  paymentMethod?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export function useRPFundingSummary(params?: RPFundingSummaryParams) {
  return useQuery({
    queryKey: ['rp', 'funding-summary', params],
    queryFn: () =>
      rpApi.get<{ data: RPFundingEntry[] }>('/funding-summary', params as Record<string, string | number | undefined>),
  });
}

// ── Mutations ──

export function useCreateRPCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<RPCustomer>) => rpApi.post<{ data: RPCustomer }>('/customers', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rp', 'customers'] }),
  });
}

export function useCreateRPInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      customerId: string;
      locationId?: string;
      dueDate?: string;
      memo?: string;
      lines: { description: string; quantity: number; unitPrice: number }[];
    }) => rpApi.post<{ data: RPInvoice }>('/invoices', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'invoices'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useUpdateRPInvoiceStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      rpApi.put<{ data: RPInvoice }>(`/invoices/${id}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'invoices'] });
      qc.invalidateQueries({ queryKey: ['rp', 'invoice'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useRPInvoice(id: string | null) {
  return useQuery({
    queryKey: ['rp', 'invoice', id],
    queryFn: () => rpApi.get<{ data: RPInvoice & { lines: RPInvoiceLine[] } }>(`/invoices/${id}`),
    enabled: !!id,
  });
}

export function useUpdateRPInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      customerId: string;
      dueDate?: string;
      memo?: string;
      lines: { description: string; quantity: number; unitPrice: number }[];
    }) => rpApi.put<{ data: RPInvoice }>(`/invoices/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'invoices'] });
      qc.invalidateQueries({ queryKey: ['rp', 'invoice'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useDeleteRPInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rpApi.delete(`/invoices/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'invoices'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useRPPaymentLinks(params?: {
  status?: string;
  customerId?: string;
  locationId?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['rp', 'payment-links', params],
    queryFn: () =>
      rpApi.get<{ data: RPPaymentLink[]; total: number }>('/payment-links', params as Record<string, string | number>),
  });
}

export function useRPPaymentLink(id: string | null) {
  return useQuery({
    queryKey: ['rp', 'payment-link', id],
    queryFn: () => rpApi.get<{ data: RPPaymentLink }>(`/payment-links/${id}`),
    enabled: !!id,
  });
}

export function useCreateRPPaymentLink() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: async (data: {
      customerId: string;
      dueDate: string;
      amount: number;
      invoiceId?: string;
      notes?: string;
      locationId?: string;
      merchantId?: string;
      isRecurring?: boolean;
      recurringFrequency?: string;
      recurringStartAt?: string;
      recurringEndAt?: string | null;
      statementDescriptor?: string;
    }) => {
      // Read frontend_url from global_settings so the RP service builds
      // the correct Pay By Link URL in the email (not the browser's origin).
      const settings = await api.get<{ data: Record<string, string | null> }>('/settings');
      const appUrl = settings.data?.frontend_url ?? undefined;
      return rpApi.post<{ data: RPPaymentLink }>('/payment-links', {
        ...data,
        appUrl,
        initiatedByEmail: user?.email ?? undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'payment-links'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useUpdateRPPaymentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      customerId?: string;
      dueDate?: string;
      amount?: number;
      invoiceId?: string | null;
      notes?: string;
      status?: string;
      recurringFrequency?: string;
      recurringStartAt?: string;
      recurringEndAt?: string | null;
    }) => rpApi.put<{ data: RPPaymentLink }>(`/payment-links/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'payment-links'] });
      qc.invalidateQueries({ queryKey: ['rp', 'payment-link'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useUpdateRPPaymentLinkStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      rpApi.put<{ data: RPPaymentLink }>(`/payment-links/${id}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'payment-links'] });
      qc.invalidateQueries({ queryKey: ['rp', 'payment-link'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useDeleteRPPaymentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rpApi.delete(`/payment-links/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'payment-links'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useUpdateRPCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<RPCustomer> & { id: string }) =>
      rpApi.put<{ data: RPCustomer }>(`/customers/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'customers'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useDeleteRPCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ success: boolean }>(`/customers/${id}/deactivate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'customers'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useActivateRPCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put<{ success: boolean }>(`/customers/${id}/activate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'customers'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

export function useRecordRPPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { invoiceId: string; amount: number; paymentMethod?: string; memo?: string }) =>
      rpApi.post<{ data: RPReceivedPayment }>('/payments', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rp', 'payments'] });
      qc.invalidateQueries({ queryKey: ['rp', 'invoices'] });
      qc.invalidateQueries({ queryKey: ['rp', 'invoice'] });
      qc.invalidateQueries({ queryKey: ['rp', 'dashboard'] });
    },
  });
}

// ── Customer Payment Methods ──

export function useRPCustomerCards(customerId: string | null) {
  return useQuery({
    queryKey: ['rp', 'customer-cards', customerId],
    queryFn: () => rpApi.get<{ data: RPCustomerCard[] }>(`/customers/${customerId}/cards`),
    enabled: !!customerId,
  });
}

export function useRPCustomerBanks(customerId: string | null) {
  return useQuery({
    queryKey: ['rp', 'customer-banks', customerId],
    queryFn: () => rpApi.get<{ data: RPCustomerBank[] }>(`/customers/${customerId}/banks`),
    enabled: !!customerId,
  });
}

export function useCreateRPCustomerCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, ...data }: Omit<RPCustomerCard, 'id' | 'tenantId' | 'isActive' | 'createdAt'>) =>
      rpApi.post<{ data: RPCustomerCard }>(`/customers/${customerId}/cards`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['rp', 'customers'] });
      qc.invalidateQueries({ queryKey: ['rp', 'customer-cards', vars.customerId] });
    },
  });
}

export function useUpdateRPCustomerCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, id, ...data }: Partial<RPCustomerCard> & { customerId: string; id: string }) =>
      rpApi.put<{ data: RPCustomerCard }>(`/customers/${customerId}/cards/${id}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['rp', 'customer-cards', vars.customerId] });
    },
  });
}

export function useDeleteRPCustomerCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, id }: { customerId: string; id: string }) =>
      rpApi.delete(`/customers/${customerId}/cards/${id}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['rp', 'customer-cards', vars.customerId] });
    },
  });
}

export function useCreateRPCustomerBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, ...data }: Omit<RPCustomerBank, 'id' | 'tenantId' | 'isActive' | 'createdAt'>) =>
      rpApi.post<{ data: RPCustomerBank }>(`/customers/${customerId}/banks`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['rp', 'customers'] });
      qc.invalidateQueries({ queryKey: ['rp', 'customer-banks', vars.customerId] });
    },
  });
}

export function useUpdateRPCustomerBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, id, ...data }: Partial<RPCustomerBank> & { customerId: string; id: string }) =>
      rpApi.put<{ data: RPCustomerBank }>(`/customers/${customerId}/banks/${id}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['rp', 'customer-banks', vars.customerId] });
    },
  });
}

export function useDeleteRPCustomerBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, id }: { customerId: string; id: string }) =>
      rpApi.delete(`/customers/${customerId}/banks/${id}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['rp', 'customer-banks', vars.customerId] });
    },
  });
}

// ── Customer Primary Payment Methods ──

export interface CustomerPrimaryMethods {
  primaryCardId: string | null;
  primaryBankId: string | null;
}

export function useCustomerPrimaryMethods(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-primary-methods', customerId],
    queryFn: () => api.get<{ data: CustomerPrimaryMethods }>(`/customer-primary-methods/${customerId}`),
    enabled: !!customerId,
  });
}

export function useSetPrimaryCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, cardId }: { customerId: string; cardId: string }) =>
      api.put(`/customer-primary-methods/${customerId}/primary-card`, { cardId }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['customer-primary-methods', vars.customerId] });
    },
  });
}

export function useSetPrimaryBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, bankId }: { customerId: string; bankId: string }) =>
      api.put(`/customer-primary-methods/${customerId}/primary-bank`, { bankId }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['customer-primary-methods', vars.customerId] });
    },
  });
}

// ── NACHA ──

export interface RPNachFileRecord {
  record: {
    id: string;
    nachFileId: string;
    sequenceIndex: string;
    indicator: number;
    batchNumber: number | null;
    customerId: string | null;
    customerName: string | null;
    locationId: string | null;
    paymentId: string | null;
    routingNumber: string | null;
    accountNumber: string | null;
    transactionAmount: string | null;
    transactionCode: number | null;
    addenda: string | null;
    createdAt: string;
  };
  fileSequence: string | null;
  locationId: string | null;
}

export function useRPNachFileRecords(params?: { nachFileId?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['rp', 'nach-file-records', params],
    queryFn: () =>
      rpApi.get<{ data: RPNachFileRecord[] }>('/nach/file-records', params as Record<string, string | number>),
  });
}

// ── Public pay page hooks (no auth cookie required) ──
// Uses plain fetch instead of rpApi to avoid the 401→login redirect
// that fires when there is no session cookie.

async function publicFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`/rp-api/v1${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options?.headers ?? {}) },
  });
  const json = await res.json();
  if (!res.ok) throw json;
  return json;
}

export function usePublicPaymentLink(publicToken: string) {
  return useQuery({
    queryKey: ['rp', 'pay', publicToken],
    queryFn: () => publicFetch<{ data: PublicPaymentLinkData }>(`/pay/${publicToken}`),
    retry: false,
    staleTime: 0,
  });
}

export function useSubmitPublicPayment(publicToken: string) {
  return useMutation({
    mutationFn: (body: PublicPaySubmitData) =>
      publicFetch<{ data: PaymentConfirmation }>(`/pay/${publicToken}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    meta: { suppressGlobalError: true },
  });
}
